---
title: "How an AI agent pays for a single API call: x402, explained with code"
description: "HTTP 402 sat unused for 25 years. x402 revives it so an AI agent can pay for one API call inline, no account or card. Here is the handshake, with code."
date: "2026-06-22"
author: "Kevin Wang"
tags: ["x402", "agent-payments", "api", "tutorial"]
keyword: "ai agent payments"
ogImage: "/og/x402-agent-payments.png"
---

export const bar = (label, value) => ({ label, value });

export const topServices = [
  bar("StableEnrich", 3120),
  bar("BlockRun YOPO", 2680),
  bar("HYRE Agent", 1420),
];

export const txReality = [
  bar("Feb 2026 spike", 3800000),
  bar("Typical day", 131000),
];

export const x402Timeline = [
  { date: "1999", title: "402 reserved, then unused" },
  { date: "May 2025", title: "Coinbase ships x402", highlight: true },
  { date: "Dec 2025", title: "v2: spec, SDK, facilitators" },
  { date: "Mar 2026", title: "Stripe + Tempo ship MPP" },
  { date: "Apr 2026", title: "Linux Foundation takes it over", highlight: true },
];

export const x402Actors = ["Agent", "Server", "Facilitator", "Chain"];

export const x402Flow = [
  { from: "Agent", to: "Server", label: "request, no payment" },
  { from: "Server", to: "Agent", label: "402 + price", highlight: true },
  { from: "Agent", to: "Server", label: "retry, signed X-PAYMENT" },
  { from: "Server", to: "Facilitator", label: "verify signature" },
  { from: "Server", label: "run the work" },
  { from: "Server", to: "Chain", label: "settle on-chain" },
  { from: "Server", to: "Agent", label: "200 OK + data" },
];

An AI agent finds the perfect API for one task. To call it, the API wants an account, a saved card, and a monthly plan.

The agent has none of those. It cannot fill a signup form, store a card, or wait for a human to approve an invoice.

Almost every paid API on the web assumes a person is behind the keyboard. x402 drops that assumption.

It revives an HTTP status code that sat unused for 25 years, so an agent can **pay for a single call inline, with no account at all.**

<Callout type="tip" title="Key findings">

- **x402 turns HTTP 402 "Payment Required" into a real payment handshake.** A server answers an unpaid request with `402` and the price; the client pays and retries.
- **The agent pays per call, with no account, key, or subscription.** Payment is a signed message in a header, not a checkout flow.
- **It settles in stablecoins (USDC) on a fast chain.** The signature is gasless for the payer; a facilitator submits it on-chain.
- **It is one of several agent-payment standards.** Stripe's MPP, Google's AP2, and the older L402 sit alongside it, and some of them interoperate.

</Callout>

## What x402 actually is

x402 is an open standard for paying over HTTP. It is built around `402 Payment Required`, a status code the spec has reserved "for future use" since the late 1990s.

[RFC 9110](https://www.rfc-editor.org/rfc/rfc9110.html#section-15.5.3) (June 2022) still says exactly that. No convention ever filled the code in, and x402 is the one that finally did.

Coinbase published x402 on May 6, 2025, under an Apache-2.0 license. A year later it outgrew a single vendor.

On April 2, 2026, the [Linux Foundation](https://www.linuxfoundation.org/) took over hosting it as the x402 Foundation. Google, Visa, Mastercard, AWS, Microsoft, Shopify, Circle, and the Solana Foundation are all named participants.

<Timeline
  items={x402Timeline}
  caption="x402 from dormant status code to foundation-governed standard. The May 2025 launch and the April 2026 Linux Foundation move are the load-bearing dates."
/>

The mechanism is small on purpose. A server advertises a price on a `402`; a client attaches a signed payment and asks again.

The server verifies the payment, does the work, and returns the result. That is the whole protocol, and the rest is detail.

## Why an agent can't just use a credit card

Card rails were built for humans, and they break in two ways when the buyer is software.

First, an agent cannot complete the human parts: the signup, the saved card, the email receipt, the dispute flow.

Second, the economics are wrong for one API call. Card networks carry per-transaction minimums in the tens of cents, so charging two-tenths of a cent loses money on fees alone.

x402 fits the shape of the request instead. Payment is one signed message, created and sent by the same code that makes the call, for an amount as small as a fraction of a cent.

That is why the agent-payment forecasts run so large. [McKinsey](https://www.mckinsey.com/) projected (October 17, 2025) that AI agents could mediate $3 trillion to $5 trillion of consumer commerce by 2030.

That is a forecast, not a meter reading. Later in this post I show what the real numbers look like today.

The honest framing: cards give you chargebacks and buyer protection x402 has no equivalent for, and x402 gives you per-call settlement cards cannot price. **They solve different problems.**

## The payment handshake, step by step

Here is the full round trip, end to end.

<SequenceDiagram
  actors={x402Actors}
  rows={x402Flow}
  caption="The x402 handshake across the four parties. The 402 challenge (step 2) is the move that makes it work."
/>

Now the same thing in code. The client makes a normal request with no payment:

```bash
curl -i -X POST https://api.example.com/v1/run/some.endpoint \
  -H "Content-Type: application/json" \
  -d '{"query": "anything"}'
```

The server answers `402 Payment Required` and, in the body, the exact terms of payment:

```json
{
  "x402Version": 1,
  "error": "X-PAYMENT header is required",
  "accepts": [
    {
      "scheme": "exact",
      "network": "base",
      "maxAmountRequired": "2000",
      "resource": "https://api.example.com/v1/run/some.endpoint",
      "description": "some.endpoint",
      "mimeType": "application/json",
      "payTo": "0xC0ffee...Payee",
      "maxTimeoutSeconds": 60,
      "asset": "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
      "extra": { "name": "USD Coin", "version": "2" }
    }
  ]
}
```

Read the fields, because they are the contract.

`scheme: "exact"` means pay this amount, no more, no less. `maxAmountRequired: "2000"` is in atomic units, so 2000 at six decimals is 0.002 USDC, two-tenths of a cent.

`asset` is the USDC contract on Base, `payTo` is the recipient, and `maxTimeoutSeconds` is how long the quote holds. `accepts` is an array, so a server can offer several networks or prices and let the client pick.

The client builds a payment, base64-encodes it into an `X-PAYMENT` header, and sends the identical request again:

```bash
curl -X POST https://api.example.com/v1/run/some.endpoint \
  -H "Content-Type: application/json" \
  -H "X-PAYMENT: eyJ4NDAyVmVyc2lvbiI6MSwic2NoZW1lIjoiZXhhY3QiLC..." \
  -d '{"query": "anything"}'
```

If the payment checks out, the server runs the work and returns `200 OK` with the result, plus an `X-PAYMENT-RESPONSE` header carrying the on-chain settlement. **One extra round trip, and the agent paid for exactly what it used.**

Nobody really hand-rolls that header. The official client SDK wraps `fetch` and runs the 402-sign-retry loop for you:

```ts
import { wrapFetchWithPayment } from "x402-fetch";
import { privateKeyToAccount } from "viem/accounts";

// the agent's wallet, funded with a few dollars of USDC
const account = privateKeyToAccount(process.env.AGENT_PRIVATE_KEY);
const fetchWithPay = wrapFetchWithPayment(fetch, account);

// looks like a normal fetch; the 402, the signature, and the retry are automatic
const res = await fetchWithPay("https://api.example.com/v1/run/some.endpoint", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ query: "anything" }),
});

const data = await res.json();
```

From the agent's side it is one call. The wallet, the 402, and the retry are plumbing.

## What the signature actually authorizes

The payment is not a transaction the agent broadcasts. It is a signed authorization that the server, or its facilitator, submits on the agent's behalf.

On an EVM chain, x402 uses [EIP-3009](https://eips.ethereum.org/EIPS/eip-3009) `transferWithAuthorization`. The client signs an EIP-712 typed message that says "move this much USDC from me to the payee," and someone else pays the gas to put it on-chain.

So the agent's wallet only needs USDC, not the chain's native gas token. The decoded `X-PAYMENT` payload is roughly this:

```json
{
  "x402Version": 1,
  "scheme": "exact",
  "network": "base",
  "payload": {
    "signature": "0x9f8e...",
    "authorization": {
      "from": "0xAgentWallet...",
      "to": "0xC0ffee...Payee",
      "value": "2000",
      "validAfter": "0",
      "validBefore": "1781490000",
      "nonce": "0x4c1d...e7"
    }
  }
}
```

The `nonce` and `validBefore` are the replay protection: a signature is good once, and only until it expires.

The server, or a facilitator like Coinbase's hosted one, calls `/verify` before doing any work and `/settle` after. The default token is USDC, and the production networks are Base and Solana.

Two design choices are worth naming. The `exact` scheme is the common one, but the spec also describes `upto` (authorize a ceiling, charge for actual usage) and batch settlement (collect many tiny payments, settle once on-chain).

The verify-then-settle split also matters. A server can confirm payment is good, run the work, and only settle if the work succeeds.

## x402 is one of several agent-payment standards

x402 is not the only attempt to let software pay software. The press framing of "x402 versus everything" misses that several of these sit at different layers and compose.

The useful split is by what each one actually moves:

| Standard | Backed by | Rail | Account to pay? | Status |
| --- | --- | --- | --- | --- |
| **x402** | Coinbase, now Linux Foundation | Stablecoin (USDC) on Base/Solana | No | Live since May 2025 |
| **MPP** | Stripe and Tempo | Stablecoin, cards, Lightning | No | Launched March 2026 |
| **Google AP2** | Google plus 60+ partners | Rail-agnostic (signed mandates) | Tied to a user | Announced September 2025 |
| **Stripe/OpenAI ACP** | Stripe and OpenAI | Cards | Buyer enters card in chat | Live in ChatGPT |
| **L402** | Lightning Labs | Bitcoin Lightning | No | Production since 2020 |

A few notes the headlines skip. Stripe's [Machine Payments Protocol](https://stripe.com/) (MPP), launched March 18, 2026, also revives HTTP 402, and Stripe says the core x402 `exact` flow maps onto its model.

Google's [AP2](https://ap2-protocol.org/) does not move money at all; it produces a signed mandate that any rail, including x402, can settle against. [L402](https://github.com/lightninglabs/L402) is the elder here, in production on Bitcoin Lightning since 2020.

So this is less a winner-take-all race than a set of overlapping standards, some of them built to interoperate.

## How much of this is real, and its limits

The institutional lineup is real. **The volume is small.**

That gap is the most important thing to understand before you bet a product on it, and most write-ups skip it.

Reported cumulative transactions range from 75 million to 100 million depending on the source and date, with no single audited figure.

The daily reality is more sobering. [CoinDesk](https://www.coindesk.com/) reported (March 11, 2026) only about $28,000 in genuine daily volume, "much of it from testing and 'gamed' transactions rather than real commerce."

A February 2026 spike to millions of transactions was attributed to infrastructure testing and wash trading, not buyers.

<Chart caption="x402 daily transaction count: the February 2026 spike (attributed to infrastructure testing and wash trading) against a typical day. Source: CoinDesk / Artemis, March 2026.">
  <BarChart
    data={txReality}
    baseline={0}
    showValues
    valueSuffix=""
    format={(n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : `${Math.round(n / 1e3)}k`)}
    ariaLabel="x402 daily transactions: February spike versus a typical day"
  />
</Chart>

Strip out the noise and the real recurring demand is tiny but legible.

Over 30 days in mid-2026, on-chain data put genuine usage in the low single-digit thousands of dollars per service. It clusters in exactly the place the standard was designed for: agents buying API, data, and inference calls.

<Chart caption="The top recurring x402 services by 30-day on-chain volume, in USD, mid-2026. The dollar amounts are small, and they cluster around agents buying data and inference APIs. Source: x402scan via x402 Inc., May 2026.">
  <BarChart
    data={topServices}
    baseline={0}
    showValues
    valueSuffix=""
    format={(n) => `$${n.toLocaleString()}`}
    ariaLabel="Top x402 services by 30-day on-chain volume in USD"
  />
</Chart>

There are real critiques under the hood, too.

EIP-3009 only works with tokens that implement it, which leaves out USDT and DAI and even a large share of USDC (a later extension widens this).

Facilitators carry the gas and infrastructure cost with no protocol-level fee, which raises the obvious question of who keeps them running once the subsidies end.

Most traffic flows through one hosted facilitator today, which sits awkwardly next to the decentralization pitch. None of these are fatal, and none are resolved.

## What it looks like from the server side

We wired x402 into our own [API gateway](/catalog), so the server half of this is not hypothetical for me.

The interesting part was not emitting the `402`. It was deciding when to charge.

The gateway prices each challenge from the exact request body, because some endpoints charge per input item. So the unpaid challenge and the paid retry have to carry the same body, or the amount the agent signed will not match the work it gets.

The other choice that mattered: verify the payment, run the work, and only settle on-chain if the run succeeds. A failed call settles nothing, so the agent is never charged for data it did not receive.

That asymmetry, easy to get wrong, is the whole reason per-call payment feels fair instead of risky. It is the same per-request, [USD-priced model](/pricing) the rest of the [catalog](/catalog) already uses, with the wallet swapped in for the API key.

The point is bigger than our gateway. **x402 lets any server quote a price in a `402` and any agent pay it in a header,** and that is a genuinely new thing on the web, whoever ships it.

## Frequently asked questions

**Is x402 free to use?**

The protocol is open and free. The cost is the payment itself plus on-chain fees. Coinbase's hosted facilitator gives 1,000 free transactions per month, then charges $0.001 per transaction, with chain gas paid separately. You can also run your own facilitator and avoid that fee.

**Is x402 an official standard?**

It is an open standard, Apache-2.0 licensed, and since April 2, 2026 it is hosted by the Linux Foundation with participants including Google, Visa, Mastercard, AWS, and Circle. It is not an IETF RFC. Stripe's MPP, by contrast, is on the IETF standards track.

**What blockchain does x402 use?**

The production networks are Base (an Ethereum layer 2) and Solana, with USDC as the default token. Self-hosted facilitators add other EVM chains. On EVM it uses EIP-3009 signed authorizations, and on Solana it uses a partially-signed transaction where the facilitator pays the fee.

**Can an AI agent pay without holding a wallet?**

Not with x402 directly: the agent needs a wallet and a private key to sign payments. That is a real operational cost, since funding and securing that key is now part of running the agent. Custodial wallet services and platforms like Coinbase's CDP exist to manage that key on the agent's behalf.

**How do I add x402 to my own API?**

Add the payment middleware for your framework (Express, Hono, Next.js, FastAPI, and Go are supported), set a price and a payee address per route, and point it at a facilitator for verification and settlement. The middleware emits the `402` and checks payments; you do not touch chain code unless you self-host the facilitator.

<CTA to="/catalog" note="Every endpoint priced in plain USD, payable per call. New accounts start with free credit, so you can wire up an agent before funding a wallet.">
  Browse the data catalog
</CTA>
